import * as React from 'react';
import { ChangeEventHandler, DragEventHandler, useRef } from 'react';
import cn from 'clsx';

import * as styles from './styles.css';
import { FileContent } from './components/FileContent';
import { EmptyContent } from './components/EmptyContent';

export interface IUploaderProps {
  file: File | null;
  handleDrag: DragEventHandler<HTMLInputElement>;
  handleLeave: DragEventHandler<HTMLInputElement>;
  handleDrop: DragEventHandler<HTMLInputElement>;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  handleClickCross: (e: React.MouseEvent) => void;
  dragActive: boolean;
  dragDisabled?: boolean;
  hasError?: boolean;
  isFileUploading?: boolean;
  isDisabled?: boolean;
  isMultiple?: boolean;
}

export const Uploader: React.FC<IUploaderProps> = ({
  file,
  handleChange,
  handleDrag,
  handleLeave,
  handleDrop,
  handleClickCross,
  hasError = false,
  isFileUploading = false,
  dragActive,
  dragDisabled,
  isDisabled,
  isMultiple = false,
}) => {
  const hasFile = Boolean(file);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleReload = (e: React.MouseEvent) => {
    e.preventDefault();

    if (inputRef?.current) {
      inputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <div
      className={cn(
        styles.root,
        hasError && styles.error,
        dragActive && styles['active'],
        (dragDisabled || isDisabled) && styles['disabled'],
        hasFile && styles['has-file'],
        isFileUploading && styles['uploading'],
      )}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleLeave}
      onDrop={handleDrop}
    >
      <FileContent
        file={file}
        handleClickCross={handleClickCross}
        handleReload={handleReload}
        hasError={hasError}
        isFileUploading={isFileUploading}
      />

      <EmptyContent
        isShow={!hasFile}
        dragActive={dragActive}
        isDisabled={isDisabled}
        inputRef={inputRef}
        handleChange={handleChange}
        isMultiple={isMultiple}
      />
    </div>
  );
};

Uploader.displayName = 'Uploader';
